import { Button, Form, FormInput, useZodForm } from '@gymcoach/ui';
import { describeError } from '@gymcoach/shared';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Splash } from '@/shell/FullScreen';

import { AuthFrame } from './AuthFrame';
import { useAuth } from './AuthProvider';
import { authCopy } from './copy';
import { NewPasswordForm } from './NewPasswordForm';
import { PasswordField } from './PasswordField';
import { parseRecoveryFragment, type RecoveryFragment } from './recovery';
import { useRequestPasswordReset, useStartRecoverySession } from './usePassword';

const c = authCopy.signIn;

const emailField = z.string().trim().min(1, c.emailRequired).email(c.emailInvalid);
const signInSchema = z.object({ email: emailField, password: z.string().min(1, c.passwordRequired) });
const forgotSchema = z.object({ email: emailField });

type Mode = 'sign-in' | 'forgot' | 'forgot-sent' | 'recovering' | 'recovery' | 'recovery-failed';

/** Wrong email or password reads the same for both: never say which one was wrong. */
function signInError(error: unknown): unknown {
  const code = (error as { code?: string } | null)?.code;
  const message = (error as { message?: string } | null)?.message ?? '';
  if (code === 'invalid_credentials' || /invalid login credentials/i.test(message)) {
    return { code: 'P0001', message: c.wrong };
  }
  // A trial gym that ended bans its accounts (migration 20261020000400).
  if (code === 'user_banned' || /user is banned/i.test(message)) {
    return { code: 'P0001', message: authCopy.trialEnded };
  }
  return error;
}

/**
 * Staff sign-in (DESIGN.md A1): email + password, "Forgot password?" by email, and the
 * page the reset link comes back to (`/login#…type=recovery`), which asks for a new
 * password in the same frame. Public route: /login.
 */
export function SignInPage() {
  const { session, user, isLoading, signInWithPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';

  // Read the reset link's fragment once, then take it out of the address bar.
  const [fragment] = useState<RecoveryFragment>(() => parseRecoveryFragment(window.location.hash));
  const [mode, setMode] = useState<Mode>(() =>
    fragment.kind === 'session' ? 'recovering' : fragment.kind === 'error' ? 'recovery-failed' : 'sign-in',
  );
  const [error, setError] = useState<unknown>(null);
  const [sentTo, setSentTo] = useState('');

  const startRecovery = useStartRecoverySession();
  const requestReset = useRequestPasswordReset();
  // Validate on submit: an "Enter your email" appearing on blur would shift the page
  // under the pointer and swallow a click on "Forgot password?".
  const signInForm = useZodForm(signInSchema, { defaultValues: { email: '', password: '' }, mode: 'onSubmit' });
  const forgotForm = useZodForm(forgotSchema, { defaultValues: { email: '' }, mode: 'onSubmit' });

  useEffect(() => {
    if (fragment.kind === 'none') return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    if (fragment.kind !== 'session') return;
    startRecovery.mutate(
      { accessToken: fragment.accessToken, refreshToken: fragment.refreshToken },
      { onSuccess: () => setMode('recovery'), onError: () => setMode('recovery-failed') },
    );
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invite = fragment.kind === 'session' && fragment.purpose === 'invite';
  // Signed up for the trial again with an address that has an account (request-demo):
  // greet them, rather than say "reset your password" to someone who never asked.
  const returning = !invite && new URLSearchParams(location.search).get('welcome') === 'back';
  const r = authCopy.recovery;
  const greeting = invite
    ? { checking: r.checkingInvite, title: r.inviteTitle, subtitle: r.inviteSubtitle }
    : returning
      ? { checking: r.checkingReturning, title: r.returningTitle, subtitle: r.returningSubtitle }
      : { checking: r.checking, title: r.title, subtitle: r.subtitle };
  if (isLoading || mode === 'recovering') return <Splash label={greeting.checking} />;
  if (session && mode !== 'recovery') return <Navigate to={from} replace />;

  async function onSignIn(values: z.infer<typeof signInSchema>) {
    setError(null);
    try {
      await signInWithPassword(values.email, values.password);
      navigate(from, { replace: true });
    } catch (caught) {
      setError(signInError(caught));
      // Keep the email; clear the password (DESIGN.md A1).
      signInForm.resetField('password');
      signInForm.setFocus('password');
    }
  }

  async function onForgot(values: z.infer<typeof forgotSchema>) {
    try {
      const email = await requestReset.mutateAsync(values.email);
      setSentTo(email);
      setMode('forgot-sent');
    } catch {
      // Shown by <Form> from requestReset.error.
    }
  }

  function openForgot() {
    forgotForm.setValue('email', signInForm.getValues('email'));
    requestReset.reset();
    setMode('forgot');
  }

  function backToSignIn() {
    setError(null);
    setMode('sign-in');
  }

  if (mode === 'recovery') {
    return (
      <AuthFrame>
        <h1 className="text-2xl font-bold tracking-tight text-ink">{greeting.title}</h1>
        <p className="mt-1.5 text-base text-ink-muted">{greeting.subtitle}</p>
        <div className="mt-5">
          <NewPasswordForm email={user?.email ?? null} onDone={() => navigate('/', { replace: true })} />
        </div>
      </AuthFrame>
    );
  }

  if (mode === 'recovery-failed') {
    const why = fragment.kind === 'error' ? fragment.description : describeError(startRecovery.error).message;
    return (
      <AuthFrame>
        <h1 className="text-2xl font-bold tracking-tight text-ink">{authCopy.recovery.expiredTitle}</h1>
        <p className="mt-1.5 text-base text-ink-muted">{authCopy.recovery.expired}</p>
        {why && <p className="mt-2 text-sm text-ink-subtle">{why}</p>}
        <div className="mt-5 flex flex-col gap-2">
          <Button variant="primary" size="lg" fullWidth onClick={openForgot}>
            {authCopy.recovery.again}
          </Button>
          <Button variant="ghost" fullWidth onClick={backToSignIn}>
            {authCopy.forgot.back}
          </Button>
        </div>
      </AuthFrame>
    );
  }

  if (mode === 'forgot-sent') {
    return (
      <AuthFrame footer={authCopy.forgot.noEmail}>
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-[14px] bg-accent-subtle text-accent" aria-hidden="true">
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-ink">{authCopy.forgot.sentTitle}</h1>
        <p className="mt-1.5 text-base text-ink-muted" role="status">
          {authCopy.forgot.sent(sentTo)}
        </p>
        <Button variant="secondary" size="lg" fullWidth className="mt-5" onClick={backToSignIn}>
          {authCopy.forgot.back}
        </Button>
      </AuthFrame>
    );
  }

  if (mode === 'forgot') {
    return (
      <AuthFrame footer={authCopy.forgot.noEmail}>
        <h1 className="text-2xl font-bold tracking-tight text-ink">{authCopy.forgot.title}</h1>
        <p className="mt-1.5 text-base text-ink-muted">{authCopy.forgot.subtitle}</p>
        <Form form={forgotForm} onSubmit={onForgot} error={requestReset.error} className="mt-5" ariaLabel={authCopy.forgot.title}>
          <FormInput
            form={forgotForm}
            name="email"
            label={c.email}
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder={c.emailPlaceholder}
            autoFocus
            className="h-11 text-lg sm:h-10 sm:text-base"
          />
          <Button type="submit" variant="primary" size="lg" fullWidth loading={forgotForm.formState.isSubmitting} loadingLabel={authCopy.forgot.submitting}>
            {authCopy.forgot.submit}
          </Button>
          <Button variant="ghost" fullWidth onClick={backToSignIn}>
            {authCopy.forgot.back}
          </Button>
        </Form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <h1 className="text-2xl font-bold tracking-tight text-ink">{c.title}</h1>
      <p className="mt-1.5 text-base text-ink-muted">{c.subtitle}</p>
      <Form form={signInForm} onSubmit={onSignIn} error={error} className="mt-5" ariaLabel={c.title}>
        <FormInput
          form={signInForm}
          name="email"
          label={c.email}
          type="email"
          autoComplete="username"
          inputMode="email"
          placeholder={c.emailPlaceholder}
          autoFocus
          className="h-11 text-lg sm:h-10 sm:text-base"
        />
        <div className="flex flex-col gap-1.5">
          <PasswordField form={signInForm} name="password" label={c.password} autoComplete="current-password" required={false} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={openForgot}
              className="rounded text-sm font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              {c.forgot}
            </button>
          </div>
        </div>
        <Button type="submit" variant="primary" size="lg" fullWidth loading={signInForm.formState.isSubmitting} loadingLabel={c.submitting}>
          {c.submit}
        </Button>
      </Form>
    </AuthFrame>
  );
}
